/**
 * Period logic of the period picker (TZ 8.12.4 quick ranges and comparison mode) and comparison periods of 9.4:
 * a day compares with the same weekday of the previous week; YoY uses the day 364 days earlier (same weekday);
 * longer periods compare with the preceding period of the same length.
 * All values are trading-day labels "YYYY-MM-DD" (inclusive).
 */
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { parseTradingDate, toTradingDateString } from '../format/time_format';

export const QUICK_RANGE_KEYS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'this_month',
  'last_month',
  'this_year',
  'custom',
] as const;

export type QuickRangeKey = (typeof QUICK_RANGE_KEYS)[number];

/**
 * 9.4 comparison modes, the same values as the API's `compare` (center/metrics/query_models.py CompareMode):
 * prev = the preceding equal period (one day -> same weekday last week), prev_day = the day before, prev_week = the
 * same days one week earlier, prev_month = the same day numbers one month earlier, yoy = 364 days earlier.
 */
export const COMPARISON_MODES = ['prev', 'prev_day', 'prev_week', 'prev_month', 'yoy', 'none'] as const;
export type ComparisonMode = (typeof COMPARISON_MODES)[number];

/** 9.4: YoY compares with the day 364 days earlier so the weekday matches. */
export const YEAR_OVER_YEAR_DAY_OFFSET = 364;
const DAYS_IN_WEEK = 7;

export interface DateRange {
  from: string;
  to: string;
}

export function isQuickRangeKey(candidate: unknown): candidate is QuickRangeKey {
  return typeof candidate === 'string' && (QUICK_RANGE_KEYS as readonly string[]).includes(candidate);
}

export function isComparisonMode(candidate: unknown): candidate is ComparisonMode {
  return typeof candidate === 'string' && (COMPARISON_MODES as readonly string[]).includes(candidate);
}

const TRADING_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function isTradingDateString(candidate: unknown): candidate is string {
  return (
    typeof candidate === 'string' &&
    TRADING_DATE_PATTERN.test(candidate) &&
    !Number.isNaN(parseTradingDate(candidate).getTime())
  );
}

/** Resolves a quick range relative to "today" (trading day in the company zone). */
export function resolveQuickRange(rangeKey: Exclude<QuickRangeKey, 'custom'>, today: string): DateRange {
  const todayDate = parseTradingDate(today);
  const toLabel = toTradingDateString;
  switch (rangeKey) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const yesterday = toLabel(addDays(todayDate, -1));
      return { from: yesterday, to: yesterday };
    }
    case 'last_7_days':
      return { from: toLabel(addDays(todayDate, -6)), to: today };
    case 'last_30_days':
      return { from: toLabel(addDays(todayDate, -29)), to: today };
    case 'this_month':
      return { from: toLabel(startOfMonth(todayDate)), to: today };
    case 'last_month': {
      const previousMonthDate = addMonths(todayDate, -1);
      return { from: toLabel(startOfMonth(previousMonthDate)), to: toLabel(endOfMonth(previousMonthDate)) };
    }
    case 'this_year':
      return { from: toLabel(startOfYear(todayDate)), to: today };
  }
}

export function rangeLengthInDays(dateRange: DateRange): number {
  return differenceInCalendarDays(parseTradingDate(dateRange.to), parseTradingDate(dateRange.from)) + 1;
}

/** A range of whole calendar months ("1 Aug – 30 Sep"): compared month by month (9.4). */
export function isWholeMonths(dateRange: DateRange): boolean {
  const toDate = parseTradingDate(dateRange.to);
  return dateRange.from.endsWith('-01') && toTradingDateString(endOfMonth(toDate)) === dateRange.to;
}

/** Number of calendar months a whole-month range covers. */
export function monthCountOf(dateRange: DateRange): number {
  const fromDate = parseTradingDate(dateRange.from);
  const toDate = parseTradingDate(dateRange.to);
  return (toDate.getFullYear() - fromDate.getFullYear()) * 12 + toDate.getMonth() - fromDate.getMonth() + 1;
}

/**
 * Comparison period for the selected range (9.4), the same rule as center/metrics/periods.py so the panel names
 * exactly the dates the API compares: one day -> the same weekday last week; whole calendar months -> the same
 * number of preceding months; any other range -> the directly preceding range of the same length; YoY -> 364 days
 * earlier (weekday aligned). Returns null for mode "none".
 */
export function comparisonRange(dateRange: DateRange, comparisonMode: ComparisonMode): DateRange | null {
  const fromDate = parseTradingDate(dateRange.from);
  const toDate = parseTradingDate(dateRange.to);
  if (comparisonMode === 'none') {
    return null;
  }
  if (comparisonMode === 'yoy') {
    return shiftedRange(dateRange, YEAR_OVER_YEAR_DAY_OFFSET);
  }
  if (comparisonMode === 'prev_day') {
    return shiftedRange(dateRange, 1);
  }
  if (comparisonMode === 'prev_week') {
    return shiftedRange(dateRange, DAYS_IN_WEEK);
  }
  if (comparisonMode === 'prev_month') {
    return previousMonthRange(dateRange);
  }
  const lengthInDays = rangeLengthInDays(dateRange);
  if (lengthInDays === 1) {
    const sameWeekdayLastWeek = toTradingDateString(addDays(fromDate, -DAYS_IN_WEEK));
    return { from: sameWeekdayLastWeek, to: sameWeekdayLastWeek };
  }
  if (isWholeMonths(dateRange)) {
    // Days are paired by day number, so the last partner month ends at the shorter of the two month lengths
    // (September 1-30 is compared with August 1-30), exactly as the API's compare_from / compare_to.
    const monthCount = monthCountOf(dateRange);
    const partnerStart = startOfMonth(addMonths(fromDate, -monthCount));
    const partnerLastMonthEnd = endOfMonth(addMonths(partnerStart, monthCount - 1));
    const lastPairedDay = Math.min(partnerLastMonthEnd.getDate(), toDate.getDate());
    return {
      from: toTradingDateString(partnerStart),
      to: toTradingDateString(
        new Date(partnerLastMonthEnd.getFullYear(), partnerLastMonthEnd.getMonth(), lastPairedDay),
      ),
    };
  }
  return {
    from: toTradingDateString(addDays(fromDate, -lengthInDays)),
    to: toTradingDateString(addDays(toDate, -lengthInDays)),
  };
}

/**
 * The compared day of one day of the selected range under the same pairing as `comparisonRange` and the API
 * (center/metrics/periods.py): shifted by the period length, a week, a day or 364 days; whole months and "last month"
 * pair by day number, and a day the shorter month lacks has no partner (null). Used to name the actual compared date
 * of every point of a dashed comparison line ("25.09: 221", owner 2026-10-05).
 */
export function comparisonPartnerDay(
  dateRange: DateRange,
  comparisonMode: ComparisonMode,
  tradingDay: string,
): string | null {
  const currentDate = parseTradingDate(tradingDay);
  const shifted = (shiftInDays: number) => toTradingDateString(addDays(currentDate, -shiftInDays));
  const sameDayNumberMonthsBack = (monthCount: number) => {
    const partnerMonthStart = startOfMonth(addMonths(currentDate, -monthCount));
    return currentDate.getDate() <= endOfMonth(partnerMonthStart).getDate()
      ? toTradingDateString(
          new Date(partnerMonthStart.getFullYear(), partnerMonthStart.getMonth(), currentDate.getDate()),
        )
      : null;
  };
  switch (comparisonMode) {
    case 'none':
      return null;
    case 'yoy':
      return shifted(YEAR_OVER_YEAR_DAY_OFFSET);
    case 'prev_day':
      return shifted(1);
    case 'prev_week':
      return shifted(DAYS_IN_WEEK);
    case 'prev_month':
      return sameDayNumberMonthsBack(1);
    case 'prev': {
      const lengthInDays = rangeLengthInDays(dateRange);
      if (lengthInDays === 1) {
        return shifted(DAYS_IN_WEEK);
      }
      return isWholeMonths(dateRange)
        ? sameDayNumberMonthsBack(monthCountOf(dateRange))
        : shifted(lengthInDays);
    }
  }
}

/**
 * The compared dates as they are actually covered when the data of the selected stores starts inside them
 * (owner 2026-10-05: a comparison never reaches before the first data day). `dataStart` after the range end means
 * nothing is left; null `dataStart` keeps the range.
 */
export function clipRangeToDataStart(
  comparedRange: DateRange,
  dataStart: string | null | undefined,
): DateRange {
  if (dataStart === null || dataStart === undefined || dataStart <= comparedRange.from) {
    return comparedRange;
  }
  return { from: dataStart > comparedRange.to ? comparedRange.to : dataStart, to: comparedRange.to };
}

function shiftedRange(dateRange: DateRange, shiftInDays: number): DateRange {
  return {
    from: toTradingDateString(addDays(parseTradingDate(dateRange.from), -shiftInDays)),
    to: toTradingDateString(addDays(parseTradingDate(dateRange.to), -shiftInDays)),
  };
}

/** Same day numbers one month earlier; days the shorter month lacks have no partner (as in the API). */
function previousMonthRange(dateRange: DateRange): DateRange | null {
  const partnerDays: string[] = [];
  for (let dayOffset = 0; dayOffset < rangeLengthInDays(dateRange); dayOffset += 1) {
    const currentDate = addDays(parseTradingDate(dateRange.from), dayOffset);
    const partnerMonthStart = startOfMonth(addMonths(currentDate, -1));
    if (currentDate.getDate() <= endOfMonth(partnerMonthStart).getDate()) {
      partnerDays.push(
        toTradingDateString(
          new Date(partnerMonthStart.getFullYear(), partnerMonthStart.getMonth(), currentDate.getDate()),
        ),
      );
    }
  }
  const firstPartnerDay = partnerDays[0];
  const lastPartnerDay = partnerDays[partnerDays.length - 1];
  return firstPartnerDay === undefined || lastPartnerDay === undefined
    ? null
    : { from: firstPartnerDay, to: lastPartnerDay };
}

/**
 * The comparison options offered for a range (owner review 2026-10-01). One day: the day before, the same weekday
 * last week (default), the same day last year, none. A range: the preceding equal period, the same days last week
 * (ranges shorter than a week; for a whole week it equals the preceding period), the same days last month (a part
 * of one calendar month), the same period last year, none.
 */
export function availableComparisonModes(dateRange: DateRange): ComparisonMode[] {
  const lengthInDays = rangeLengthInDays(dateRange);
  if (lengthInDays === 1) {
    return ['prev_day', 'prev', 'yoy', 'none'];
  }
  const withinOneMonth = dateRange.from.slice(0, 7) === dateRange.to.slice(0, 7);
  return [
    'prev',
    ...(lengthInDays < DAYS_IN_WEEK ? (['prev_week'] as const) : []),
    ...(withinOneMonth && !isWholeMonths(dateRange) ? (['prev_month'] as const) : []),
    'yoy',
    'none',
  ];
}

/** Orders the two ends of a custom range and clamps the end to today. */
export function normalizeCustomRange(dateRange: DateRange, today: string): DateRange {
  const [earlierDate, laterDate] =
    dateRange.from <= dateRange.to ? [dateRange.from, dateRange.to] : [dateRange.to, dateRange.from];
  return { from: earlierDate, to: laterDate > today ? today : laterDate };
}

/** Suggested chart grain for a range: hours for one or two days, days up to ~3 months, then weeks/months. */
export function suggestedGrain(dateRange: DateRange): 'hour' | 'day' | 'week' | 'month' {
  const lengthInDays = rangeLengthInDays(dateRange);
  if (lengthInDays <= 2) {
    return 'hour';
  }
  if (lengthInDays <= 92) {
    return 'day';
  }
  if (lengthInDays <= 366) {
    return 'week';
  }
  return 'month';
}
